import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ElectricBoltOutlinedIcon from "@mui/icons-material/ElectricBoltOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import RadarIcon from "@mui/icons-material/Radar";
import { useNavigate, useSearchParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import ContentWrapper from "../Layout/ContentWrapper";
import { enterContestAction, getStatusAction } from "../../store/reducers/app";

const Overview = (props) => {
  const {} = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  // Keep the active contest in the URL when navigating away from Overview.
  const contestId = searchParams.get("contest_id");
  const contestQuery = contestId ? `?contest_id=${contestId}` : "";
  const userId = useSelector((state) => state.app?.userId);
  // Guards against a double-tap firing two enter-contest calls while the first
  // is still in flight.
  const [isEntering, setIsEntering] = useState(false);

  // A Knockout-type contest has no group stage (surfaced from /status into app
  // state). When true we drop the Group Stage card, the group + 3rd-place
  // scoring lines, and the group-flow copy, and point the CTA at the knockout
  // picks instead of group picks.
  const contestType = useSelector((state) => state.app?.contestType);
  const isKnockout = contestType === "Knockout";

  // Scoring weights + maxima come from the API (derived from the `rounds`
  // table), so editing `rounds` changes these cards with no code change.
  const scoring = useSelector((state) => state.app?.scoring);
  const w = scoring?.weights;
  const scaleLine = w
    ? `R16 ${w.r16} · QF ${w.qf} · SF ${w.sf} · 3rd ${w.third} · Final ${w.final}`
    : "";

  const OverviewCard = (props) => (
    <Grid
      container
      direction="column"
      spacing="24px"
      sx={[
        {
          width: "100%",
          padding: "32px",
          borderRadius: "16px",
          border: `1px solid ${theme.branding.gray[200]}`,
          backgroundColor: theme.branding.gray[0],
        },
        (theme) =>
          theme.applyStyles("dark", {
            border: `1px solid ${theme.branding.darkBlue[600]}`,
            backgroundColor: theme.branding.darkBlue[800],
          }),
      ]}
    >
      <Grid container direction="row">
        <Grid>{props.titleIcon}</Grid>
        <Grid container direction="column" sx={{ gap: 0 }}>
          <Typography variant="h4" sx={{ fontSize: "24px" }}>
            {props.title}
          </Typography>
          {props.subtitle && (
            <Typography
              variant="body2"
              sx={[
                (theme) =>
                  theme.applyStyles("dark", {
                    color: theme.branding.gray[300],
                  }),
              ]}
            >
              {props.subtitle}
            </Typography>
          )}
        </Grid>
      </Grid>
      {/* map out bullet points for each box */}
      <Grid container direction="column" sx={{ gap: "12px" }}>
        {props.bullets.map((bullet, key) => (
          <Grid
            container
            direction="row"
            wrap="nowrap"
            key={`${props.title[0]}-bullet-${key}`}
            sx={{ gap: "12px" }}
          >
            {/* bullet point number */}
            <Grid
              container
              xs={1}
              sx={{
                backgroundColor: theme.branding.cyan[300],
                borderRadius: "100%",
                height: "24px",
                width: "24px",
                minWidth: "24px",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: theme.branding.gray[0] }}
              >
                {key + 1}
              </Typography>
            </Grid>
            {/* bullet point text */}
            <Grid container>
              <Typography
                variant="body1"
                sx={[
                  (theme) =>
                    theme.applyStyles("dark", {
                      color: theme.branding.gray[200],
                    }),
                ]}
              >
                {bullet}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );

  return (
    <ContentWrapper>
      <Grid container>
        <Grid
          container
          direction="column"
          sx={{
            maxWidth: theme.breakpoints.values.lg,
            justifyContent: "center",
            alignItems: "center",
            gap: "48px",
          }}
        >
          {/* row one */}
          <Grid
            container
            direction="column"
            sx={{
              padding: "16px 32px 16px",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "1088px",
              gap: "32px",
            }}
          >
            {/* trophy circle icon */}
            <Grid
              container
              sx={[
                {
                  width: isMobile ? "120px" : "160px",
                  height: isMobile ? "120px" : "160px",
                  padding: "0px 10px",
                  borderRadius: "100%",
                  background: `linear-gradient(135deg, ${theme.branding.cyan[300]} 0%, ${theme.branding.cyan[700]} 100%)`,
                  boxShadow: `0px 12px 30px 0px  ${theme.branding.cyan[600]}`,
                  justifyContent: "center",
                  alignItems: "center",
                },
                (theme) =>
                  theme.applyStyles("dark", {
                    boxShadow: `0px 12px 30px 0px  ${theme.branding.cyan[800]}`,
                  }),
              ]}
            >
              <EmojiEventsOutlinedIcon
                sx={{
                  height: isMobile ? "52px" : "64px",
                  width: isMobile ? "52px" : "64px",
                  color: theme.branding.gray[0],
                  alignSelf: "center",
                }}
              />
            </Grid>
            {/* title text */}
            <Grid
              container
              direction="column"
              sx={{
                justifyContent: "center",
                alignItems: "center",
                gap: "16px",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontSize: isMobile ? "36px" : "48px",
                  textAlign: "center",
                }}
              >
                Welcome to the 2026 World Cup Bracket Challenge
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: isMobile ? "16px" : "20px",
                  textAlign: "center",
                  color: theme.branding.gray[400],
                }}
              >
                {isKnockout
                  ? "Predict the knockout bracket. Win Prizes."
                  : "Pick group winners. Predict the Knockout Contest. Win Prizes."}
              </Typography>
            </Grid>
          </Grid>

          {/* row two: 2 boxes */}
          <Grid
            container
            direction={isMobile ? "column" : "row"}
            wrap="nowrap"
            sx={{ gap: "32px" }}
          >
            {!isKnockout && (
            <OverviewCard
              titleIcon={
                <Grid
                  container
                  sx={{
                    borderRadius: "14px",
                    height: "48px",
                    width: "48px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: `linear-gradient(135deg, ${theme.branding.green[300]} 0%,  ${theme.branding.green[500]} 100%);`,
                  }}
                >
                  <GroupOutlinedIcon
                    sx={{
                      color: theme.branding.gray[0],
                      height: "24px",
                      width: "24px",
                    }}
                  />
                </Grid>
              }
              title="Group Stage"
              subtitle="Phase 1"
              bullets={[
                <span>
                  <strong>12 Groups • 4 Teams Each</strong> - Round robin format
                  where each team plays 3 matches"
                </span>,
                <span>
                  Rank teams <strong>1st through 4th</strong> in each group
                  based on your predictions
                </span>,
                <span>
                  Top 2 teams from each group{" "}
                  <span style={{ color: theme.branding.green[500] }}>
                    <strong>automatically qualify</strong>
                  </span>
                </span>,
                <span>
                  After group picks,{" "}
                  <strong>rank the 12 third-place teams</strong> — top 8 advance
                  as wildcards
                </span>,
              ]}
            />
            )}
            <OverviewCard
              titleIcon={
                <Grid
                  container
                  sx={{
                    borderRadius: "14px",
                    height: "48px",
                    width: "48px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: `linear-gradient(135deg, ${theme.branding.cyan[300]} 0%,  ${theme.branding.cyan[700]} 100%);`,
                  }}
                >
                  <RadarIcon
                    sx={{
                      color: theme.branding.gray[0],
                      height: "24px",
                      width: "24px",
                    }}
                  />
                </Grid>
              }
              title="Knockout Contest"
              bullets={[
                <span>
                  <strong>32-Team Single Elimination</strong>
                </span>,
                <span>
                  Picks begin at the second round, "Round of 16"
                </span>,
                <span>
                  <strong>Later rounds are worth more</strong> — points climb
                  each round, so a deep run pays off
                </span>,
              ]}
            />
          </Grid>
          <Grid container>
            <OverviewCard
              titleIcon={
                <Grid
                  container
                  sx={{
                    borderRadius: "14px",
                    height: "48px",
                    width: "48px",
                    justifyContent: "center",
                    alignItems: "center",
                    background: `linear-gradient(135deg, ${theme.branding.purple[500]} 0%,  ${theme.branding.hotPink[500]} 100%);`,
                  }}
                >
                  <EmojiEventsOutlinedIcon
                    sx={{
                      color: theme.branding.gray[0],
                      height: "24px",
                      width: "24px",
                    }}
                  />
                </Grid>
              }
              title="Scoring & Prizes"
              subtitle="How to Win"
              bullets={
                isKnockout
                  ? [
                      <span>
                        Knockout Contest: <strong>16 Picks</strong> — each
                        correct advance scales by round ({scaleLine})
                      </span>,
                      <span>
                        Maximum possible:{" "}
                        <span style={{ color: theme.branding.gold[500] }}>
                          <strong>{scoring?.total_max ?? 0} points</strong>
                        </span>{" "}
                        -- Addition tiebreaker question
                      </span>,
                    ]
                  : [
                      <span>
                        Group Stage:{" "}
                        <strong>
                          Up to {scoring?.group_top2_max ?? 0} points
                        </strong>{" "}
                        ({w?.group_stage ?? 1} pt for each top-2 team you
                        correctly send through — 2 × 12 groups)
                      </span>,
                      <span>
                        3rd Place Wildcards:{" "}
                        <strong>Up to {scoring?.third_max ?? 0} points</strong>{" "}
                        ({w?.group_stage ?? 1} pt per correct pick among your 8
                        best third-place teams)
                      </span>,
                      <span>
                        Knockout Contest:{" "}
                        <strong>Up to {scoring?.ko_max ?? 0} points</strong> —
                        each correct advance scales by round ({scaleLine})
                      </span>,
                      <span>
                        Maximum possible:{" "}
                        <span style={{ color: theme.branding.gold[500] }}>
                          <strong>{scoring?.total_max ?? 0} points</strong>
                        </span>
                      </span>,
                    ]
              }
            />
          </Grid>

          {/* row 3: how it works  */}
          <Grid
            container
            direction="column"
            spacing="16px"
            sx={[
              {
                width: "100%",
                padding: "32px",
                borderRadius: "16px",
                border: `2px solid ${theme.branding.cyan[300]}`,
                backgroundColor: theme.branding.gray[50],
              },
              (theme) =>
                theme.applyStyles("dark", {
                  backgroundColor: theme.branding.darkBlue[800],
                }),
            ]}
          >
            <Grid
              container
              direction="row"
              sx={{ gap: "8px", alignItems: "center" }}
            >
              <ElectricBoltOutlinedIcon
                sx={[
                  {
                    width: "20px",
                    height: "20px",
                    color: theme.branding.cyan[500],
                  },
                  (theme) =>
                    theme.applyStyles("dark", {
                      color: theme.branding.gray[0],
                    }),
                ]}
              />
              <Typography variant="h4">How it Works</Typography>
            </Grid>
            <Grid container direction="column" sx={{ gap: "8px" }}>
              {!isKnockout && (
                <>
                  <Typography
                    variant="body1"
                    sx={[
                      (theme) =>
                        theme.applyStyles("dark", {
                          color: theme.branding.gray[200],
                          fontWeight: "700",
                        }),
                    ]}
                  >
                    The bracket is NOT finalized until group play ends.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={[
                      (theme) =>
                        theme.applyStyles("dark", {
                          color: theme.branding.gray[200],
                        }),
                    ]}
                  >
                    You'll first pick group outcomes in <strong>Phase 1</strong>,
                    then the knockout bracket will auto-generate based on your
                    picks.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={[
                      (theme) =>
                        theme.applyStyles("dark", {
                          color: theme.branding.gray[200],
                        }),
                    ]}
                  >
                    In <strong>Phase 2</strong>, you can adjust your knockout
                    predictions before final submission.
                  </Typography>
                </>
              )}
              {isKnockout && (
                <>
                  <Typography
                    variant="body1"
                    sx={[
                      (theme) =>
                        theme.applyStyles("dark", {
                          color: theme.branding.gray[200],
                          fontWeight: "700",
                        }),
                    ]}
                  >
                    Pick which team advances in each knockout matchup — no
                    scorelines, just winners.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={[
                      (theme) =>
                        theme.applyStyles("dark", {
                          color: theme.branding.gray[200],
                        }),
                    ]}
                  >
                    Advance teams round by round through the 32-team bracket, all
                    the way to the champion.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={[
                      (theme) =>
                        theme.applyStyles("dark", {
                          color: theme.branding.gray[200],
                        }),
                    ]}
                  >
                    Later rounds are worth more, so a deep run pays off.
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>

          {/* row 4: rewards */}
          <Grid
            container
            direction="column"
            spacing="16px"
            sx={{
              width: "100%",
              padding: "32px",
              borderRadius: "16px",
              background: `linear-gradient(170deg, ${theme.branding.purple[500]} 6.19%,  ${theme.branding.hotPink[500]} 93.81%);`,
            }}
          >
            <Grid
              container
              direction="row"
              sx={{
                gap: "8px",
                alignItems: "center",
              }}
            >
              <AutoAwesomeOutlinedIcon
                sx={{
                  width: "32px",
                  height: "32px",
                  color: theme.branding.gray[0],
                }}
              />
              <Grid container direction="column" wrap="nowrap" sx={{ gap: 0 }}>
                <Typography
                  variant="h4"
                  sx={{ fontSize: "24px", color: theme.branding.gray[0] }}
                >
                  Exclusive Rewards
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: theme.branding.gray[0] }}
                >
                  Compete for status & recognition
                </Typography>
              </Grid>
            </Grid>
            {/* rewards box */}
            <Grid
              container
              direction={isMobile ? "column" : "row"}
              wrap="nowrap"
              sx={{
                gap: "16px",
                height: isMobile ? "auto" : "182px",
              }}
            >
              <Grid
                container
                direction="column"
                spacing="12px"
                sx={{
                  height: "fit-content",
                  width: "100%",
                  padding: "16px",
                  borderRadius: "14px",
                  border: `1px solid #FDC7004D`, //30% opacity gold[300]
                  background: `linear-gradient(220deg, ${theme.branding.purple[500]} 0%,  ${theme.branding.hotPink[500]} 100%);`,
                }}
              >
                <Grid container direction="row">
                  <Grid
                    container
                    sx={{
                      borderRadius: "14px",
                      height: "48px",
                      width: "48px",
                      justifyContent: "center",
                      alignItems: "center",
                      background: `linear-gradient(135deg, ${theme.branding.gold[300]} 0%,  ${theme.branding.gold[500]} 100%);`,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ color: theme.branding.gray[0] }}
                    >
                      $
                    </Typography>
                  </Grid>
                  <Typography
                    variant="h4"
                    sx={{ fontSize: "24px", color: theme.branding.gray[0] }}
                  >
                    Cash Prizes
                  </Typography>
                </Grid>
                <Grid container direction="column" spacing="6px">
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: theme.branding.gray[0] }}
                  >
                    🥇 <strong>1st Place</strong> Amazon Certificate $50
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: theme.branding.gray[0] }}
                  >
                    🥈 <strong>2nd Place</strong> Amazon Certificate $25
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            {/* continue button  */}
            <Button
              variant="outlined"
              onClick={() => {
                navigate(`/rules${contestQuery}`);
              }}
              sx={{
                border: "1px solid #FFFFFF4D", //30% opacity gray[0]
                borderRadius: "14px",
                backgroundColor: "#FFFFFF33", //20% opacity gray[0]
                width: "100%",
                padding: "20px 48px",
              }}
            >
              <Typography
                variant="button"
                sx={{ color: theme.branding.gray[0] }}
              >
                View All Rewards & Rules →
              </Typography>
            </Button>
          </Grid>

          <Grid
            container
            direction="column"
            sx={{
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              disabled={isEntering}
              onClick={async () => {
                // The Picks button IS the "enter contest" action — mark entry
                // server-side (flips entry_page for next visit) before
                // navigating, so a user who enters but hasn't picked anything
                // yet still lands on Picks, not back on Overview.
                setIsEntering(true);
                try {
                  const res = await dispatch(
                    enterContestAction({ userId, contestId }),
                  );
                  if (res?.meta?.requestStatus === "fulfilled") {
                    // entry_page/focus only refresh correctly by re-pulling
                    // status — enter_contest's own response isn't shaped like
                    // getStatus's, so we don't try to parse it into state.
                    await dispatch(getStatusAction({ userId, contestId }));
                  }
                } catch (err) {
                  console.error("Error marking contest entry: ", err);
                } finally {
                  setIsEntering(false);
                }
                navigate(
                  `${isKnockout ? "/picks/knockout" : "/picks/group"}${contestQuery}`,
                );
              }}
              sx={{
                borderRadius: "100px",
                background: `linear-gradient(90deg, ${theme.branding.cyan[300]} 0%,  ${theme.branding.cyan[700]} 100%);`,
                padding: "20px 48px",
                maxWidth: "272px",
              }}
            >
              <Typography variant="h4" sx={{ color: theme.branding.gray[0] }}>
                Start Making Picks →
              </Typography>
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </ContentWrapper>
  );
};

export default Overview;
